<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contractServices',
    path: '/contract-services',
    component: 'ContractServices',
    title: () => gettext('Contract services'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractServices',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    skip_archived: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('Contract', 'Contract number'),
      sortPath: 'contract_revision.contract_number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_revision.contract_number',
              source: 'row',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            skipExport: true,
            params: {
              id: 'contract_id',
            },
          },
        ],
      },
    },
    {
      id: 'contract_type',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_type',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.contract_type',
            variantMap: {
              rental: 'contract-type--rental',
              vendor: 'contract-type--vendor',
              rental_agreement: 'contract-type--rental_agreement',
              service: 'contract-type--service',
              parking: 'contract-type--parking',
              unknown: 'contract-type--unknown',
            },
          },
        ],
      },
    },
    {
      id: 'contract_status',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'status',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.status',
            variantMap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('ContractServices', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
    {{~#if $value.house_number }} | {{ $value.house_number }}{{/if }}\n\
    {{~#if $value.area }} ({{$value.area}}{{$value.unit_of_area}}){{/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental_id',
            },
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('Contract', 'Company'),
      sortPath: 'company.name',
      formatter: {
        name: 'chain',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'counterparty',
      name: $fn.value.pgettext('Contract', 'Counterparty'),
      sortPath: 'counterparty.name',
      formatter: {
        name: 'chain',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('Contracts', 'Valid From'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('Contracts', 'Valid To'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'next_billing_due_date',
      name: $fn.value.pgettext('Contracts', 'Next Bill'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('ContractService', 'Name'),
      type: 'command',
    },
    {
      field: 'invoice_notes',
      name: $fn.value.pgettext('ContractService', 'Invoice notes'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractService', 'Description'),
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('ContractService', 'Intermediated service'),
      formatter: 'checkbox',
    },
    {
      field: 'billing_frequency',
      name: $fn.value.pgettext('ContractService', 'Billing frequency'),
    },
    {
      field: 'billing_currency',
      name: $fn.value.pgettext('ContractService', 'Billing currency'),
    },
    {
      field: 'billing_date_rule',
      name: $fn.value.pgettext('ContractService', 'Billing date'),
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if $value }}\n\
    {{~#if $value.description }}\n\
      {{ $value.description }}\n\
    {{ else }}\n\
      {{~#if $value.anchor }}{{ $value.anchor }}{{/if }}\n\
      {{~#if $value.offset_days }}\n\
        {{ $value.offset_days }}\n\
      {{/if }}\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
    {
      field: 'due_date_rule',
      name: $fn.value.pgettext('ContractService', 'Due date'),
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if $value }}\n\
    {{~#if $value.description }}\n\
      {{ $value.description }}\n\
    {{ else }}\n\
      {{~#if $value.anchor }}{{ $value.anchor }}{{/if }}\n\
      {{~#if $value.offset_days }}\n\
        {{ $value.offset_days }}\n\
      {{/if }}\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
    {
      field: 'exchange_rate_date_rule',
      name: $fn.value.pgettext('ContractService', 'Exchange rate date'),
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if $value }}\n\
    {{~#if $value.anchor }}{{ $value.anchor }}{{/if }}\n\
\n\
    {{~#if $value.description }}{{ $value.description }}{{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/list_contract_service', {
      parameters: () => ({ ...dsParams.value, date: $fn.value.today() }),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'ContractServices.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Contract services'),
      icon: $config.value.contract.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4uejs58w2_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_86rmlgih5_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_jrqq3briu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractService',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_4nrv98toq_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'name';
  };
  const act_7172922356531116_reloadGrid_di8ufu0ua_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'contractServices',
    };
  };
  const inst_evh_7172922356531116_close_rddyci4ah_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7172922356531116_showModal_rtxuwbip1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkContractServiceEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_4uejs58w2 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4uejs58w2_getActionArgs,
    });
  const inst_evh_7315092382398562_close_bq9yx26b2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4uejs58w2,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_bq9yx26b2],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kynqkuz7c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x5p2kwvl6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kynqkuz7c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x5p2kwvl6],
    event: 'reload',
  });
  const act_2248226175642056_setData_86rmlgih5 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_86rmlgih5_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_20r9u8b0n =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_86rmlgih5,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_20r9u8b0n],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_jrqq3briu =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_jrqq3briu_getActionArgs,
    });
  const inst_evh_2248226175642056_command_4nrv98toq =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_jrqq3briu,
      event: 'command',
      when: inst_evh_2248226175642056_command_4nrv98toq_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_4nrv98toq],
    event: 'command',
  });
  const act_7172922356531116_reloadGrid_di8ufu0ua =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7172922356531116_reloadGrid_di8ufu0ua_getActionArgs,
    });
  const inst_evh_7172922356531116_close_rddyci4ah =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_reloadGrid_di8ufu0ua,
      event: 'close',
      when: inst_evh_7172922356531116_close_rddyci4ah_getWhen,
      displayName: 'reloadGrid',
    });
  const act_7172922356531116_showModal_rtxuwbip1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_rtxuwbip1_getActionArgs,
      events: [inst_evh_7172922356531116_close_rddyci4ah],
    });
  const inst_evh_7172922356531116_click_vz6q6mm1s =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_rtxuwbip1,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7172922356531116_click_vz6q6mm1s],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_bq9yx26b2,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x5p2kwvl6,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_20r9u8b0n,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_4nrv98toq,
    inst_evh_2248226175642056_command,
    inst_evh_7172922356531116_close_rddyci4ah,
    inst_evh_7172922356531116_click_vz6q6mm1s,
    inst_evh_7172922356531116_click,
  };

  const evh_7315092382398562_close_bq9yx26b2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_bq9yx26b2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x5p2kwvl6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x5p2kwvl6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_20r9u8b0n = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_20r9u8b0n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_4nrv98toq = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_4nrv98toq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_close_rddyci4ah = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_close_rddyci4ah.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click_vz6q6mm1s = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click_vz6q6mm1s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractServices"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="contractServices"
      :headerTitle="pgettext('ContractServices', 'Contract services')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'contract-services')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('Contracts', 'Skip archived contracts')"
              inline
              v-model="dsParams.skip_archived"
            >
            </ItpFormCheckbox>
          </div>
          <div class="border-start ps-3 d-flex gap-2">
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.contract.createContractIcon"
              :text="pgettext('ContractServices', 'Bulk edit...')"
              size="sm"
              variant="light"
              @click="evh_7172922356531116_click($event, {})"
            >
            </ItpButton>
          </div>
          <PageHelp path="/contracts/contracts.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
